// FINAL CHAT V2 -- numeric helpers for the tutor's figures (upstream 0a347ba,
// design A15). A figure names functions; the renderer computes what the
// drawing shows (roots, vertices, crossings, sign changes), exactly as the
// reference console's `figure-math.ts` does.
//
// The console evaluates expressions with math.js. This repo cannot take
// math.js (webpack 4 / Node 16 toolchain, see the frontend-toolchain notes),
// and it does not need to: upstream only accepts `^[\w\s+\-*/^().,]+$` in an
// expression, so a small parser for exactly that alphabet covers the whole
// contract. It builds plain closures -- the text is never evaluated as code.

const FUNCTIONS = {
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  asin: Math.asin,
  acos: Math.acos,
  atan: Math.atan,
  sinh: Math.sinh,
  cosh: Math.cosh,
  tanh: Math.tanh,
  sec: x => 1 / Math.cos(x),
  csc: x => 1 / Math.sin(x),
  cot: x => 1 / Math.tan(x),
  sqrt: Math.sqrt,
  cbrt: Math.cbrt,
  abs: Math.abs,
  exp: Math.exp,
  // math.js: log(x) is natural, log(x, base) any base.
  log: (x, base) =>
    base === undefined ? Math.log(x) : Math.log(x) / Math.log(base),
  ln: Math.log,
  log10: Math.log10,
  log2: Math.log2,
  sign: Math.sign,
  floor: Math.floor,
  ceil: Math.ceil,
  round: Math.round,
}

const CONSTANTS = { pi: Math.PI, e: Math.E }

const MAX_EXPRESSION_LENGTH = 200
const TOKEN = /\s*(?:(\d+(?:\.\d*)?|\.\d+)|([A-Za-z_]\w*)|([-+*/^(),]))/y

function tokenize(source) {
  const tokens = []
  TOKEN.lastIndex = 0
  let index = 0
  while (index < source.length) {
    if (!source.slice(index).trim()) break
    TOKEN.lastIndex = index
    const match = TOKEN.exec(source)
    if (!match) throw new Error('unsupported character')
    if (match[1] !== undefined)
      tokens.push({ type: 'num', value: Number(match[1]) })
    else if (match[2] !== undefined)
      tokens.push({ type: 'id', value: match[2] })
    else tokens.push({ type: 'op', value: match[3] })
    index = TOKEN.lastIndex
  }
  return tokens
}

/**
 * Compiles a math.js-syntax expression in `x` to a function of x. Throws on
 * anything outside the supported grammar (the figure then falls back to its
 * alt text).
 * @param {string} expression
 * @returns {(x: number) => number} NaN wherever the value is not finite
 */
export function toFunction(expression) {
  if (typeof expression !== 'string' || !expression.trim()) {
    throw new Error('empty expression')
  }
  if (expression.length > MAX_EXPRESSION_LENGTH) {
    throw new Error('expression too long')
  }
  const tokens = tokenize(expression)
  let pos = 0
  const peek = () => tokens[pos]
  const isOp = value => peek() && peek().type === 'op' && peek().value === value
  const expect = value => {
    if (!isOp(value)) throw new Error(`expected ${value}`)
    pos += 1
  }

  // expr := term (('+'|'-') term)*
  function parseExpr() {
    let left = parseTerm()
    while (isOp('+') || isOp('-')) {
      const op = tokens[pos++].value
      const right = parseTerm()
      const l = left
      left = op === '+' ? x => l(x) + right(x) : x => l(x) - right(x)
    }
    return left
  }

  // A value can follow directly (implicit multiplication, as math.js allows:
  // `2x`, `3(x + 1)`, `(x - 1)(x + 2)`).
  const startsValue = () =>
    peek() && (peek().type === 'num' || peek().type === 'id' || isOp('('))

  // term := unary (('*'|'/') unary | unary)*
  function parseTerm() {
    let left = parseUnary()
    for (;;) {
      if (isOp('*') || isOp('/')) {
        const op = tokens[pos++].value
        const right = parseUnary()
        const l = left
        left = op === '*' ? x => l(x) * right(x) : x => l(x) / right(x)
      } else if (startsValue()) {
        const right = parsePower()
        const l = left
        left = x => l(x) * right(x)
      } else {
        return left
      }
    }
  }

  // unary := ('-'|'+') unary | power   (so -x^2 is -(x^2), as in math.js)
  function parseUnary() {
    if (isOp('-')) {
      pos += 1
      const operand = parseUnary()
      return x => -operand(x)
    }
    if (isOp('+')) {
      pos += 1
      return parseUnary()
    }
    return parsePower()
  }

  // power := primary ('^' unary)?   (right-associative)
  function parsePower() {
    const base = parsePrimary()
    if (isOp('^')) {
      pos += 1
      const exponent = parseUnary()
      return x => Math.pow(base(x), exponent(x))
    }
    return base
  }

  function parsePrimary() {
    const token = peek()
    if (!token) throw new Error('unexpected end')
    if (token.type === 'num') {
      pos += 1
      const value = token.value
      return () => value
    }
    if (isOp('(')) {
      pos += 1
      const inner = parseExpr()
      expect(')')
      return inner
    }
    if (token.type === 'id') {
      pos += 1
      const name = token.value
      if (isOp('(')) {
        const fn = FUNCTIONS[name]
        if (!fn) throw new Error(`unknown function ${name}`)
        pos += 1
        const args = [parseExpr()]
        while (isOp(',')) {
          pos += 1
          args.push(parseExpr())
        }
        expect(')')
        if (args.length > 2) throw new Error('too many arguments')
        return x => fn(...args.map(arg => arg(x)))
      }
      if (name === 'x') return x => x
      const lower = name.toLowerCase()
      if (Object.prototype.hasOwnProperty.call(CONSTANTS, lower)) {
        const value = CONSTANTS[lower]
        return () => value
      }
      throw new Error(`unknown symbol ${name}`)
    }
    throw new Error(`unexpected ${token.value}`)
  }

  const compiled = parseExpr()
  if (pos !== tokens.length) throw new Error('unexpected trailing input')
  return x => {
    const value = compiled(x)
    return typeof value === 'number' && Number.isFinite(value) ? value : NaN
  }
}

function bisect(fn, a, b) {
  let lo = a
  let hi = b
  let flo = fn(lo)
  for (let i = 0; i < 60; i += 1) {
    const mid = (lo + hi) / 2
    const fmid = fn(mid)
    if (fmid === 0) return mid
    if (flo < 0 === fmid < 0) {
      lo = mid
      flo = fmid
    } else {
      hi = mid
    }
  }
  return (lo + hi) / 2
}

function dedupe(values, eps = 1e-6) {
  const out = []
  values
    .slice()
    .sort((a, b) => a - b)
    .forEach(value => {
      if (!out.length || Math.abs(value - out[out.length - 1]) > eps) {
        out.push(value)
      }
    })
  return out
}

/**
 * Roots by sign change over a sampled domain, refined by bisection. A jump
 * across a pole (1/x) is not a root: the refined point must be near zero.
 * @param {(x:number)=>number} fn
 * @param {[number, number]} domain
 * @returns {number[]}
 */
export function roots(fn, domain, samples = 800) {
  const [min, max] = domain
  const step = (max - min) / samples
  const found = []
  let prev = fn(min)
  if (prev === 0) found.push(min)
  for (let i = 1; i <= samples; i += 1) {
    const x = min + i * step
    const value = fn(x)
    if (Number.isNaN(prev) || Number.isNaN(value)) {
      prev = value
      continue
    }
    if (value === 0) {
      found.push(x)
    } else if (prev !== 0 && prev < 0 !== value < 0) {
      const root = bisect(fn, x - step, x)
      const scale = Math.max(1, Math.abs(prev), Math.abs(value))
      if (Math.abs(fn(root)) < 1e-6 * scale) found.push(root)
    }
    prev = value
  }
  return dedupe(found)
}

function refine(fn, lo, hi, kind) {
  let a = lo
  let b = hi
  for (let i = 0; i < 40; i += 1) {
    const m1 = a + (b - a) / 3
    const m2 = b - (b - a) / 3
    const better = kind === 'min' ? fn(m1) < fn(m2) : fn(m1) > fn(m2)
    if (better) b = m2
    else a = m1
  }
  const x = (a + b) / 2
  return { x, y: fn(x), kind }
}

/**
 * Local extrema (a parabola's vertex among them) by sampling.
 * @returns {{x:number, y:number, kind:'min'|'max'}[]}
 */
export function extrema(fn, domain, samples = 800) {
  const [min, max] = domain
  const step = (max - min) / samples
  const out = []
  let a = fn(min)
  let b = fn(min + step)
  for (let i = 2; i <= samples; i += 1) {
    const x = min + i * step
    const c = fn(x)
    if (![a, b, c].some(Number.isNaN)) {
      if (b < a && b < c) out.push(refine(fn, x - 2 * step, x, 'min'))
      if (b > a && b > c) out.push(refine(fn, x - 2 * step, x, 'max'))
    }
    a = b
    b = c
  }
  return out
}

export function intersections(f, g, domain) {
  return roots(x => f(x) - g(x), domain).map(x => ({ x, y: f(x) }))
}

export function round(value, digits = 2) {
  const p = 10 ** digits
  const rounded = Math.round(value * p) / p
  return Object.is(rounded, -0) ? 0 : rounded
}

/**
 * The sign of each factor on each interval between the factors' roots.
 * @param {string[]} factors
 * @param {[number, number]} domain
 */
export function signTable(factors, domain) {
  const fns = factors.map(toFunction)
  const cuts = dedupe(
    fns.reduce((all, fn) => all.concat(roots(fn, domain)), [])
  ).filter(cut => cut > domain[0] && cut < domain[1])
  const bounds = [domain[0], ...cuts, domain[1]]
  const columns = bounds
    .slice(0, -1)
    .map((from, i) => ({ from, to: bounds[i + 1] }))
  const rows = fns.map((fn, i) => ({
    label: factors[i],
    signs: columns.map(({ from, to }) => Math.sign(fn((from + to) / 2))),
  }))
  const product = columns.map((_, c) =>
    rows.reduce((acc, row) => acc * row.signs[c], 1)
  )
  return { cuts, columns, rows, product }
}

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'

/**
 * A number (or a label) with Persian digits and a real minus sign.
 * @param {number|string} value
 * @param {boolean} [persian]
 */
export function formatNumber(value, persian = true) {
  const text = String(value).replace('-', '−')
  return persian ? text.replace(/\d/g, d => PERSIAN_DIGITS[Number(d)]) : text
}
